import React, { useState, useRef } from "react";
import { Row, Col, Container } from "react-bootstrap";
import PropTypes from 'prop-types';
import { customValidators, object, string, array as yuparray, number as yupnumber, date } from '../../../Helpers/Validators/customValidators';
import moment from 'moment';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Button } from '@mui/material';
import { EmojiObjects as EmojiObjectsIcon } from '@mui/icons-material';
import BeneficiaryForm from './BeneficiaryForm';
import IconHolder from "../../../Helpers/IconHolder";
import { TextInput, SwitchInput, SelectInput, StyledRadioInput } from "../../../Helpers/Inputs";
import BackNext from '../../Common/SubComponents/BackNext';
import StatesWithDisabled from '../../../Helpers/Lists/StatesWithDisabled';
import { InputMaskZip } from '../../../Helpers/Formatters';
import { ConvertStringToBoolean, ConvertBooleanToString } from "../../../Helpers/Formatters/ConvertBoolean";
import GenerateUniqueSSN from '../../../Helpers/Prefill/GenerateUniqueSSN';
import { emptyStringToUndefined } from '../../../Helpers/Formatters/EmptyStringToUndefined.jsx';
import Step6Icon from '../icons/step_6.svg?react';

export default function Beneficiary(props) {

  const formMethods = useForm({
    resolver: yupResolver(Beneficiary.ValidationSchema),
    mode: 'all',
    reValidateMode: 'all',
    defaultValues: props.formState,
  });

  const { handleSubmit, trigger, setValue } = formMethods;

  const [showBeneCountMessage, setShowBeneCountMessage] = useState(false);
  const [beneCountMessage, setBeneCountMessage] = useState("");
  const beneErrorMessage = useRef();

  const handleValueChange = (field, value) => {
    props.onFieldChange(field, value);
  };

  const submitForm = () => {
    let primaryBenePercentTotal = 0;
    let contingentBenePercentTotal = 0;
    const radix = 10;
    let primaryLength = 0;
    let contingentLength = 0;
    props.formState.beneficiaries.forEach((bene, index) => {
      primaryBenePercentTotal += bene.beneficiaryType === "Primary" ? parseInt(bene.percentage, radix) : 0;
      primaryLength += bene.beneficiaryType === "Primary" ? 1 : 0;
      contingentLength += bene.beneficiaryType === "Contingent" ? 1 : 0;
      contingentBenePercentTotal += bene.beneficiaryType === "Contingent" ? parseInt(bene.percentage, radix) : 0;
    });

    setShowBeneCountMessage(false);
    setBeneCountMessage("");
    if (primaryLength === 0) {
      setShowBeneCountMessage(true);
      setBeneCountMessage("At least 1 primary beneficiary required.");
      setTimeout(() => {
        if (beneErrorMessage.current) {
          window.scrollTo({
            top: beneErrorMessage.current.offsetTop,
            behavior: "smooth"
          });
        }
      }, 100);
      return;
    }
    if (primaryBenePercentTotal !== 100) {
      setShowBeneCountMessage(true);
      setBeneCountMessage("Primary beneficiary benefit percentage should add to 100");
      setTimeout(() => {
        if (beneErrorMessage.current) {
          window.scrollTo({
            top: beneErrorMessage.current.offsetTop,
            behavior: "smooth"
          });
        }
      }, 100);
      return;
    }
    if (contingentLength !== 0 && contingentBenePercentTotal !== 100) {
      setShowBeneCountMessage(true);
      setBeneCountMessage("Contingent beneficiary benefit percentage should add to 100");
      setTimeout(() => {
        if (beneErrorMessage.current) {
          window.scrollTo({
            top: beneErrorMessage.current.offsetTop,
            behavior: "smooth"
          });
        }
      }, 100);
      return;
    }
    props.onNext();
  };

  const addNewBeneficiary = () => {
    const newForm = Beneficiary.DefaultFormState().beneficiaries[0];
    props.onFieldChange('beneficiaries', [...props.formState.beneficiaries, newForm]);
  };

  const forms = props.formState.beneficiaries.map((beneficiaryFormState, index) =>
    <BeneficiaryForm
      formState={beneficiaryFormState}
      onRemove={() => {
        props.onFieldChange(
          'beneficiaries',
          props.formState.beneficiaries.filter((form, formIndex) => formIndex !== index)
        );
        setValue('beneficiaries', props.formState.beneficiaries.filter((form, formIndex) => formIndex !== index))
      }}
      onChange={(newForm) => {
        props.onFieldChange(
          'beneficiaries',
          props.formState.beneficiaries.map((form, formIndex) => {
            return formIndex === index ? newForm : form;
          })
        );
      }}
      trigger={trigger}
      key={index}
      index={index}
      showBeneRemove={index !== 0}
      maritalStatus={props.maritalStatus}
      beneficiaries={props.formState.beneficiaries}
    />
  );

  return (
    <Container>
      <FormProvider {...formMethods}>
        <form className="quote-form" onSubmit={handleSubmit(submitForm)}>
          <Row className="formPage justify-content-center">
            <Col lg={5}>
              <Row className="justify-content-center">
                <IconHolder icon={Step6Icon} viewBox="0 0 25 25" color='primary' />
              </Row>
              <h4 className="headerTitle">Protecting Your Mortgage or Business Loan</h4>
              <h5 className="headerSubtitle">This is the reason you are getting life insurance. Don't worry, you can change your beneficiaries at any time.</h5>

              {showBeneCountMessage && <div ref={beneErrorMessage} className="text-danger text-center mb-3">{beneCountMessage}</div>}

              <div className="switchContent ps-2">
                <div className="mb-3">
                  <SwitchInput
                    name="collaterallyAssignPolicy"
                    id="collaterallyAssignPolicy"
                    label="Collaterally Assign this Policy"
                    checked={ConvertStringToBoolean(props.formState.collaterallyAssignPolicy)}
                    onChange={e => props.onFieldChange('collaterallyAssignPolicy', ConvertBooleanToString(e.target.checked))}
                    color="primary"
                    helpText="A collateral assignment conditionally appoints a lender as the primary beneficiary to use a life insurance death benefit as collateral."
                    switchColumnSize={8}
                  />
                </div>
              </div>
            </Col>
          </Row>
          {props.formState.collaterallyAssignPolicy === "true" && (<div>
            <div className="formPageTitle">Collateral Assignment of Policy Form</div>
            <Row className="justify-content-center">
              <Col lg={5}>
                <div className="mb-3">
                  <SelectInput
                    name="collateralAssignment"
                    id="collateralAssignment"
                    value={props.formState.collateralAssignment}
                    onChange={e => handleValueChange('collateralAssignment', e.target.value)}
                    label="Assignment"
                    selectOptions={[
                      { label: "Bank", value: "07" },
                      { label: "Company", value: "01" }]}
                  />
                </div>
                <div className="mb-3">
                  <TextInput
                    name="collateralCompany"
                    id="collateralCompany"
                    label="Company/Bank Name"
                    value={props.formState.collateralCompany}
                    onChange={(e) => props.onFieldChange('collateralCompany', e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <TextInput
                    name="collateralAssignee"
                    id="collateralAssignee"
                    label="Assignee Name"
                    value={props.formState.collateralAssignee}
                    onChange={e => props.onFieldChange('collateralAssignee', e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <TextInput
                    name="collateralAssigneeAddressStreet"
                    id="collateralAssigneeAddressStreet"
                    label="Assignee Street Address"
                    value={props.formState.collateralAssigneeAddressStreet}
                    onChange={e => props.onFieldChange('collateralAssigneeAddressStreet', e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <TextInput
                    name="collateralAssigneeAddressCity"
                    id="collateralAssigneeAddressCity"
                    label="Assignee City"
                    value={props.formState.collateralAssigneeAddressCity}
                    onChange={e => props.onFieldChange('collateralAssigneeAddressCity', e.target.value)}
                  />
                </div>
                <Row>
                  <Col>
                    <div className="mb-3">
                      <SelectInput
                        name="collateralAssigneeAddressState"
                        id="collateralAssigneeAddressState"
                        value={props.formState.collateralAssigneeAddressState}
                        onChange={(e) => handleValueChange('collateralAssigneeAddressState', e.target.value)}
                        label="Assignee State"
                        selectOptions={StatesWithDisabled(["NY"])}
                      />
                    </div>
                  </Col>
                  <Col>
                    <div className="mb-3">
                      <TextInput
                        name="collateralAssigneeAddressZip"
                        id="collateralAssigneeAddressZip"
                        label="Assignee Zip"
                        value={props.formState.collateralAssigneeAddressZip}
                        onChange={e => handleValueChange('collateralAssigneeAddressZip', e.target.value)}
                        InputProps={{
                          inputComponent: InputMaskZip,
                        }}
                      />
                    </div>
                  </Col>
                </Row>
                <div className="mb-3">
                  <TextInput
                    name="collateralAssigneeEmail"
                    id="collateralAssigneeEmail"
                    label="Assignee Email"
                    value={props.formState.collateralAssigneeEmail}
                    onChange={e => props.onFieldChange('collateralAssigneeEmail', e.target.value)}
                  />
                </div>

                {props.formState.collateralAssigneeEmail && (
                  <div className="mb-3">
                    <StyledRadioInput
                      name="emailCollateralCopy"
                      id="emailCollateralCopy"
                      label="Do you want a copy of the collateral assignment sent to the assignee via email?"
                      value={props.formState.emailCollateralCopy}
                      options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                      onChange={e => handleValueChange('emailCollateralCopy', e)}
                    />
                  </div>
                )}

                <div className="mb-3">
                  <TextInput
                    name="collateralLoanNumber"
                    id="collateralLoanNumber"
                    label="Loan Number"
                    value={props.formState.collateralLoanNumber}
                    onChange={e => props.onFieldChange('collateralLoanNumber', e.target.value)}
                  />
                </div>
              </Col>
            </Row>
          </div>
          )}

          <Row className="justify-content-center">
            <Col lg={5}>
              <div className="formPageTitle">Beneficiary Information</div>

              <Alert className={`mt-3 mb-3 background-color-16`} icon={false} >
                <EmojiObjectsIcon color="primary" /> <span className="primary-color"> Insider Tip: </span> The beneficiary (the one who receives the policy benefit) can be an individual, estate, or trust, and can be changed at any time during the life of the policy.
              </Alert>

              {forms}
              {props.formState.beneficiaries && props.formState.beneficiaries.length < 6 ? (
                <Button color="primary" onClick={() => addNewBeneficiary()} className="d-flex m-auto">
                  ADD ANOTHER BENEFICIARY
                </Button>) :
                (
                  <div className="text-danger text-center">No additional Beneficiaries may be added in the eApplication.  If additional Beneficiaries are required, please submit a paper application.</div>
                )}
            </Col>
          </Row>

          <BackNext onBack={props.onBack} colSize={2} width={100} continueLabel={{ text: 'Next' }} />
        </form>
      </FormProvider>
    </Container>
  );
}

Beneficiary.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  maritalStatus: PropTypes.string.isRequired,
};

Beneficiary.ValidationSchema = object().shape({
  collaterallyAssignPolicy: string().required(),
  collateralAssignment: string().when('collaterallyAssignPolicy', {
    is: "true",
    then: (schema) => schema.required('Assignment type is required.'),
    otherwise: (schema) => schema.notRequired()
  }),
  collateralCompany: string().when('collaterallyAssignPolicy', {
    is: "true",
    then: (schema) => schema.required('Company Name is required.'),
    otherwise: (schema) => schema.notRequired()
  }),
  collateralAssignee: customValidators.string().when('collaterallyAssignPolicy', {
    is: "true",
    then: (schema) => schema.isValidNameLimitedSpecialChar('Assignee Name is required.', true, false),
    otherwise: (schema) => schema.notRequired()
  }),
  collateralAssigneeAddressStreet: string().when('collaterallyAssignPolicy', {
    is: "true",
    then: (schema) => schema
      .isValidAddress('Invalid address. No special character is allowed')
      .required('Street Address is required.'),
    otherwise: (schema) => schema.notRequired()
  }),
  collateralAssigneeAddressCity: string().when('collaterallyAssignPolicy', {
    is: "true",
    then: (schema) => schema.required('City is required.'),
    otherwise: (schema) => schema.notRequired()
  }),
  collateralAssigneeAddressState: string().when('collaterallyAssignPolicy', {
    is: "true",
    then: (schema) => schema.required('State is required.'),
    otherwise: (schema) => schema.notRequired()
  }),
  collateralAssigneeAddressZip: customValidators.string().when('collaterallyAssignPolicy', {
    is: "true",
    then: (schema) => schema
      .required('Zip is required.')
      .isValidNumbersOnly('Invalid zip.', 5),
    otherwise: (schema) => schema.notRequired()
  }),

  collateralAssigneeEmail: string()
    .email('Email address must be valid.')
    .notRequired(),

  beneficiaries: yuparray(object().shape({
    beneficiary: string()
      .required("Beneficiary is required."),
    firstName: customValidators.string().when('beneficiary', {
      is: "Individual",
      then: (schema) => schema
        .isValidNameLimitedSpecialChar('First Name is required.', true, false),
      otherwise: (schema) => schema.notRequired()
    }),
    middleInitial: string()
      .matches(/^$|^(?!\s*$)[ a-zA-ZÀ-ÖØ-öø-ÿ'.-]{1}$/, 'Must be single alpha character.')
      .notRequired(),
    lastName: customValidators.string().when('beneficiary', {
      is: "Individual",
      then: (schema) => schema
        .isValidNameLimitedSpecialChar('Last Name is required.', true, false),
      otherwise: (schema) => schema.notRequired()
    }),
    ssnOrTaxpayerID: string().when('beneficiary', {
      is: "Individual",
      then: (schema) => schema
        .matches(/(^(?:\d{3}-?\d{2}-?\d{4})?$)/, 'Social Security Number must be formatted as "123-45-6789".')
        .notRequired(),
      otherwise: (schema) => schema.notRequired()
    }),
    relationship: string().when('beneficiary', {
      is: "Individual",
      then: (schema) => schema.required('Relationship is required.'),
      otherwise: (schema) => schema.notRequired()
    }),
    dateOfBirth: date().when('beneficiary', {
      is: "Individual",
      then: (schema) => schema
        .transform(emptyStringToUndefined)
        .typeError('Invalid date.')
        .max(moment(new Date()), 'Cannot be in future.')
        .notRequired(),
      otherwise: (schema) => schema.transform(emptyStringToUndefined).notRequired()
    }),
    phoneNumber: string().when('beneficiary', {
      is: "Individual",
      then: (schema) => schema
        .matches(/^(?:(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4})?$/, 'Phone Number must be formatted as "(123) 456-7890".')
        .notRequired(),
      otherwise: (schema) => schema.notRequired()
    }),
    emailAddress: string().when('beneficiary', {
      is: "Individual",
      then: (schema) => schema.notRequired()
        .email('Email address must be valid.'),
      otherwise: (schema) => schema.notRequired()
    }),
    percentage: yupnumber()
      .typeError('Must be a number.')
      .transform(emptyStringToUndefined)
      .min(1)
      .max(100, 'Percentage must be between 1 and 100.')
      .required('Percentage is required.'),
    suffix: string().notRequired(),
    trustName: string().when('beneficiary', {
      is: "Trust",
      then: (schema) => schema.required('Trust Name is required.'),
      otherwise: (schema) => schema.notRequired()
    }),
    trustDate: date().when('beneficiary', {
      is: "Trust",
      then: (schema) => schema
        .transform(emptyStringToUndefined)
        .typeError('Invalid date')
        .required('Trust Date is required.')
        .max(moment(new Date()), 'Trust date cannot be greater than current date.'),
      otherwise: (schema) => schema.transform(emptyStringToUndefined).notRequired()
    }),
    trustIdentifyOption: string().notRequired(),
    trustSsnTaxID: string().when('trustIdentifyOption', {
      is: "SSN",
      then: (schema) => schema.matches(/(^(?:\d{3}-?\d{2}-?\d{4})?$)/, 'Social Security Number must be formatted as "123-45-6789".').notRequired(),
      otherwise: (schema) => schema.matches(/(^(?:\d{2}-?\d{7})?$)/, 'Employer Identification Number must be formatted as "12-3456789".').notRequired(),
    }),
    trustEmail: string()
      .email('Email address must be valid.')
      .notRequired(),
    trustPhoneNumber: string()
      .matches(/^(?:(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4})?$/, 'Phone Number must be formatted as "(123) 456-7890".')
      .notRequired(),
  })
  )
});

Beneficiary.DefaultFormState = (prefill = false) => {
  return {
    useAsAnSBALoan: "false",
    collaterallyAssignPolicy: "false",
    collateralLoanNumber: prefill ? "12345" : "",
    collateralAssignment: prefill ? "01" : "",
    collateralCompany: prefill ? "Americo Financial" : "",
    collateralAssignee: prefill ? "Assignee Johnson" : "",
    collateralAssigneeAddressStreet: prefill ? "501 Covr St" : "",
    collateralAssigneeAddressCity: prefill ? "Boise" : "",
    collateralAssigneeAddressState: prefill ? "ID" : "",
    collateralAssigneeAddressZip: prefill ? "83705" : "",
    collateralAssigneeEmail: prefill ? "johnson@example.com" : "",
    emailCollateralCopy: prefill ? "false" : "",

    beneficiaries: [
      {
        firstName: prefill ? 'Rebecca' : '',
        middleInitial: prefill ? 'S' : '',
        lastName: prefill ? 'Puga' : '',
        ssnOrTaxpayerID: prefill ? GenerateUniqueSSN('444') : "",
        relationship: prefill ? 'Parent' : '',
        dateOfBirth: prefill ? "09/19/1993" : "",
        phoneNumber: prefill ? "(123) 305-4567" : "",
        emailAddress: prefill ? "user.name@example.com" : "",
        percentage: prefill ? "100" : "",
        emailCollateralCopy: prefill ? "false" : "",
        beneficiary: prefill ? "Individual" : "",
        suffix: prefill ? "II" : "",
        trustName: prefill ? "Trust Demo" : "",
        trustDate: prefill ? "01/19/2020" : "",
        trustPhoneNumber: prefill ? "(321) 345-8908" : "",
        trustIdentifyOption: prefill ? "SSN" : "",
        trustSsnTaxID: prefill ? GenerateUniqueSSN('444') : "",
        trustEmail: prefill ? "trust@example.com" : "",
        beneficiaryType: "Primary"
      }
    ],
  };
};
